import { Actor, Canvas, Random, vec } from 'excalibur';
import { Theme, getTheme } from '../themes/theme';
import { paintSkinPreview } from '../themes/preview';
import { SEEDS } from '../utils/constants';
import { css, sketchStroke, wobblyArcPath, wobblyCirclePath, wobblyLinePath } from '../utils/sketch';
import { HandLabel } from './label';

export interface SkinCardOptions {
  name: string;
  theme: Theme;
  size: number;
  onPress: () => void;
}

export class SkinCard extends Actor {
  readonly themeId: string;
  private readonly cardCanvas: Canvas;
  private readonly titleLabel: HandLabel;
  private readonly statusLabel: HandLabel;
  private readonly cardSize: number;
  private readonly cardTheme: Theme;
  private selected = false;

  constructor(options: SkinCardOptions) {
    const pad = 22;
    const canvas = new Canvas({
      width: options.size + pad * 2,
      height: options.size + pad * 2,
      cache: true,
      draw: (ctx) => this.paint(ctx, pad),
    });
    super({ name: options.name, graphic: canvas });
    this.cardCanvas = canvas;
    this.cardSize = options.size;
    this.cardTheme = options.theme;
    this.themeId = options.theme.id;
    this.titleLabel = new HandLabel({
      name: `${options.name}-title`,
      text: options.theme.name,
      pos: vec(0, -options.size / 2 + 24),
      size: 30,
      tone: 'ink',
    });
    this.addChild(this.titleLabel);
    this.statusLabel = new HandLabel({
      name: `${options.name}-status`,
      text: '',
      pos: vec(0, options.size / 2 - 20),
      size: 23,
      tone: 'inkSoft',
    });
    this.addChild(this.statusLabel);
    this.on('pointerdown', () => {
      options.onPress();
      this.scale = vec(0.96, 0.96);
    });
    this.on('pointerup', () => {
      this.scale = vec(1, 1);
    });
    this.on('pointerenter', () => {
      this.scale = vec(1.03, 1.03);
    });
    this.on('pointerleave', () => {
      this.scale = vec(1, 1);
    });
  }

  setState(owned: boolean, selected: boolean): void {
    this.selected = selected;
    this.statusLabel.text = selected ? 'wearing' : owned ? 'tap to wear' : 'tap to unlock';
    this.statusLabel.setTone(selected ? 'mintDeep' : owned ? 'blueDeep' : 'inkSoft');
    this.cardCanvas.flagDirty();
  }

  refreshTheme(): void {
    this.cardCanvas.flagDirty();
    this.titleLabel.refreshTheme();
    this.statusLabel.refreshTheme();
  }

  isPointInside(x: number, y: number): boolean {
    const half = this.cardSize / 2 + 22;
    return Math.abs(x - this.pos.x) <= half && Math.abs(y - this.pos.y) <= half;
  }

  private paint(ctx: CanvasRenderingContext2D, pad: number): void {
    const rng = new Random(SEEDS.button + this.themeId.length * 13);
    const palette = getTheme().palette;
    const size = this.cardSize;
    const x = pad;
    const y = pad;
    const radius = 30;

    ctx.save();
    ctx.fillStyle = css(palette.paper, 0.97);
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(x, y, size, size, radius);
    } else {
      ctx.rect(x, y, size, size);
    }
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.translate(pad + size / 2, pad + size / 2 + 8);
    paintSkinPreview(ctx, rng, this.cardTheme, size * 0.29);
    ctx.restore();

    if (this.selected) {
      sketchStroke(ctx, rng, { color: palette.mintDeep, width: 5, alpha: 0.85 }, (c) =>
        wobblyCirclePath(c, pad + size / 2, pad + size / 2 + 8, size * 0.4, rng, 3, 44), 2, 1.5);
    }

    const edges: Array<(c: CanvasRenderingContext2D) => void> = [
      (c) => wobblyLinePath(c, x + radius, y, x + size - radius, y, rng, 1.6, 10),
      (c) => wobblyLinePath(c, x + size, y + radius, x + size, y + size - radius, rng, 1.6, 8),
      (c) => wobblyLinePath(c, x + size - radius, y + size, x + radius, y + size, rng, 1.6, 10),
      (c) => wobblyLinePath(c, x, y + size - radius, x, y + radius, rng, 1.6, 8),
      (c) => wobblyArcPath(c, x + size - radius, y + radius, radius, -Math.PI / 2, 0, rng, 1.3, 8),
      (c) => wobblyArcPath(c, x + size - radius, y + size - radius, radius, 0, Math.PI / 2, rng, 1.3, 8),
      (c) => wobblyArcPath(c, x + radius, y + size - radius, radius, Math.PI / 2, Math.PI, rng, 1.3, 8),
      (c) => wobblyArcPath(c, x + radius, y + radius, radius, Math.PI, Math.PI * 1.5, rng, 1.3, 8),
    ];
    for (const edge of edges) {
      sketchStroke(ctx, rng, { color: palette.ink, width: 3.2, alpha: 0.8 }, edge, 2, 1.5);
    }
  }
}
